import { useEffect, useState, useCallback } from 'react';
import { getUser, ensureUser, User } from './api/client';
import { initTelegramWebApp, getTelegramWebApp, haptic } from './lib/telegram';
import { Dashboard } from './pages/Dashboard';
import { CalendarPage } from './pages/CalendarPage';
import { AIAssistant } from './pages/AIAssistant';
import { WorkoutsList } from './pages/WorkoutsList';
import { WorkoutDetail } from './pages/WorkoutDetail';
import { UploadWorkout } from './pages/UploadWorkout';
import { ProgressPage } from './pages/ProgressPage';
import { ProfilePage } from './pages/ProfilePage';
import { BottomNav, TabKey } from './components/BottomNav';
import { LoadingState } from './components/States';
import './App.css';

type Page =
  | { name: 'dashboard' }
  | { name: 'calendar' }
  | { name: 'progress' }
  | { name: 'ai' }
  | { name: 'profile' }
  | { name: 'workouts' }
  | { name: 'workout-detail'; workoutId: number }
  | { name: 'upload' };

const TAB_OF_PAGE: Record<string, TabKey | null> = {
  dashboard: 'dashboard',
  calendar: 'calendar',
  progress: 'progress',
  ai: 'ai',
  profile: 'profile',
  workouts: 'dashboard',
  'workout-detail': null,
  upload: 'dashboard'
};

function App() {
  const [page, setPage] = useState<Page>({ name: 'dashboard' });
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const init = initTelegramWebApp();
    const loadUser = async () => {
      try {
        if ((init.isTestMode || !init.telegramId) && import.meta.env.DEV) {
          console.warn('Telegram ID не найден — тестовый режим');
          setUser({
            id: 1,
            telegram_id: 123456789,
            username: 'testuser',
            first_name: 'Test',
            last_name: 'User',
            created_at: new Date().toISOString()
          });
          return;
        }
        if (init.isTestMode || !init.telegramId) {
          throw new Error('Откройте Mini App через кнопку в Telegram-боте');
        }

        try {
          const u = await getUser(
            init.telegramId,
            init.user?.username,
            init.user?.first_name,
            init.user?.last_name
          );
          setUser(u);
        } catch {
          // Пытаемся создать пользователя через POST
          try {
            const u = await ensureUser(
              init.telegramId,
              init.user?.username,
              init.user?.first_name,
              init.user?.last_name
            );
            setUser(u);
          } catch {
            setUser({
              id: 0,
              telegram_id: init.telegramId,
              username: init.user?.username,
              first_name: init.user?.first_name,
              last_name: init.user?.last_name,
              created_at: new Date().toISOString()
            });
          }
        }
      } catch (err: any) {
        setError(err?.message || 'Ошибка инициализации');
      } finally {
        setLoading(false);
      }
    };
    loadUser();
  }, []);

  const navigate = useCallback((next: Page) => {
    haptic.light();
    setPage(next);
    // Прокрутка наверх при переходе
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, []);

  // Интеграция с Telegram BackButton
  useEffect(() => {
    const tg = getTelegramWebApp();
    if (!tg) return;

    const isRoot = page.name === 'dashboard' || page.name === 'progress' || page.name === 'calendar' || page.name === 'ai' || page.name === 'profile';
    if (isRoot) {
      tg.BackButton.hide();
    } else {
      tg.BackButton.show();
    }

    const onBack = () => {
      haptic.medium();
      setPage(prev => {
        if (prev.name === 'workout-detail') return { name: 'workouts' };
        if (prev.name === 'workouts') return { name: 'dashboard' };
        if (prev.name === 'upload') return { name: 'dashboard' };
        return { name: 'dashboard' };
      });
    };

    tg.BackButton.onClick(onBack);
    return () => {
      tg.BackButton.offClick(onBack);
    };
  }, [page]);

  // Активный таб для подсветки
  const activeTab: TabKey = TAB_OF_PAGE[page.name] ?? 'dashboard';
  const showBottomNav = user !== null && user.id !== 0;

  if (loading) {
    return (
      <div className="app-loading">
        <LoadingState text="Загрузка…" />
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="app-loading">
        <div className="state-block error-state">
          <div className="state-icon">!</div>
          <p className="state-title">{error || 'Не удалось загрузить данные пользователя'}</p>
          <p className="state-hint">Откройте приложение из Telegram-бота</p>
        </div>
      </div>
    );
  }

  const onTabChange = (tab: TabKey) => {
    if (tab === 'dashboard') navigate({ name: 'dashboard' });
    if (tab === 'progress') navigate({ name: 'progress' });
    if (tab === 'calendar') navigate({ name: 'calendar' });
    if (tab === 'ai') navigate({ name: 'ai' });
    if (tab === 'profile') navigate({ name: 'profile' });
  };

  return (
    <div className="app">
      <main className="app-main">
        {page.name === 'dashboard' && (
          <Dashboard
            user={user}
            onNavigate={(name, data) => {
              if (name === 'workouts') navigate({ name: 'workouts' });
              else if (name === 'workout-detail' && data?.workoutId) navigate({ name: 'workout-detail', workoutId: data.workoutId });
              else if (name === 'calendar') navigate({ name: 'calendar' });
              else if (name === 'ai') navigate({ name: 'ai' });
              else if (name === 'upload') navigate({ name: 'upload' });
            }}
          />
        )}
        {page.name === 'calendar' && (
          <CalendarPage
            userId={user.id}
            onNavigate={(name, data) => {
              if (name === 'workout-detail' && data?.workoutId) navigate({ name: 'workout-detail', workoutId: data.workoutId });
            }}
          />
        )}
        {page.name === 'progress' && (
          <ProgressPage
            userId={user.id}
            onOpenWorkout={(id) => navigate({ name: 'workout-detail', workoutId: id })}
          />
        )}
        {page.name === 'ai' && <AIAssistant userId={user.id} onNavigate={() => {}} />}
        {page.name === 'profile' && <ProfilePage user={user} />}
        {page.name === 'workouts' && (
          <WorkoutsList
            userId={user.id}
            onBack={() => navigate({ name: 'dashboard' })}
            onOpenWorkout={(id) => navigate({ name: 'workout-detail', workoutId: id })}
          />
        )}
        {page.name === 'workout-detail' && (
          <WorkoutDetail
            workoutId={page.workoutId}
            userId={user.id}
            onBack={() => navigate({ name: 'workouts' })}
          />
        )}
        {page.name === 'upload' && (
          <UploadWorkout
            user={user}
            onBack={() => navigate({ name: 'dashboard' })}
            onWorkoutCreated={(w) => {
              navigate({ name: 'workout-detail', workoutId: w.id });
            }}
          />
        )}
      </main>

      {showBottomNav && <BottomNav active={activeTab} onChange={onTabChange} />}
    </div>
  );
}

export default App;
